import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Eye, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { getUsers } from "../../../utils/getUser";
import { AuthContext } from "../../../context/authContext";
import { convertDateToCT } from "../../../utils/util";
import { listAllTrackingIds, getInboundShipmentLogs, downloadShipmentImage } from "../../../reducers/warehouse.slice";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../components/ui/dialog";
import DataTable from "../../../components/shared/DataTable";
import PageHeader from "../../../components/shared/PageHeader";
import SearchBar from "../../../components/shared/SearchBar";
import RefreshButton from "../../../components/shared/RefreshButton";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const WarehouseInbound = () => {
  const user = getUsers();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useContext(AuthContext);
  const [showLoader, setShowLoader] = useState(false);
  const [inboundDetails, setInboundDetails] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [viewTrackingId, setViewTrackingId] = useState(null);
  const [viewItems, setViewItems] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewSearch, setViewSearch] = useState("");
  const [viewPage, setViewPage] = useState(0);
  const [viewRpp, setViewRpp] = useState(5);
  const [viewImagesOpen, setViewImagesOpen] = useState(false);
  const [viewImages, setViewImages] = useState([]);
  const [viewImageIdx, setViewImageIdx] = useState(0);

  const fetchInboundItems = (trackingId) => {
    setViewLoading(true);
    dispatch(getInboundShipmentLogs(trackingId)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setViewLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedData = res?.payload?.map((item) => ({
        ...item,
        createdDate: convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm A"),
      }));
      setViewLoading(false);
      setViewItems(formattedData || []);
      setViewPage(0);
    });
  };

  const handleViewInbound = (row) => {
    setViewTrackingId(row.trackingId);
    setViewSearch("");
    setViewPage(0);
    fetchInboundItems(row.trackingId);
  };

  const handleViewImages = (rowId) => {
    setViewLoading(true);
    dispatch(downloadShipmentImage(rowId)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setViewLoading(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setViewImages(res?.payload || []);
      setViewImageIdx(0);
      setViewImagesOpen(true);
      setViewLoading(false);
    });
  };

  const viewFilteredItems = viewSearch.trim()
    ? viewItems.filter((item) => item.upc && String(item.upc).toLowerCase().includes(viewSearch.toLowerCase()))
    : viewItems;

  const viewPaginatedItems = viewFilteredItems.slice(viewPage * viewRpp, viewPage * viewRpp + viewRpp);

  const viewItemColumns = [
    { key: "item_name", label: "Product Name", width: 200 },
    { key: "upc", label: "UPC", width: 130 },
    { key: "shipment_id", label: "Shipment ID", width: 120 },
    { key: "quantity", label: "Quantity", width: 90, align: "center" },
    { key: "createdDate", label: "Shipment Date", width: 200 },
    { key: "notes", label: "Notes", width: 150 },
    {
      key: "action",
      label: "",
      width: 60,
      align: "center",
      render: (row) =>
        row?.status === "DAMAGED" ? (
          <Button variant="ghost" size="icon-sm" title="View images" onClick={() => handleViewImages(row?.id)}>
            <Images size={16} />
          </Button>
        ) : null,
    },
  ];

  const getAllItems = () => {
    setShowLoader(true);
    dispatch(listAllTrackingIds()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      const formattedOrder = res?.payload?.map((item, index) => ({
        id: `${item?.inbound_tracking_number}-${item.shipment_id}-${index + 1}`,
        trackingId: item?.inbound_tracking_number,
        shipmentId: item?.shipment_id,
        createdDate: convertDateToCT(item?.created_at, "MMMM D, YYYY hh:mm A"),
      }));
      setShowLoader(false);
      setInboundDetails(formattedOrder);
      setCurrentPage(0);
    });
  };

  useEffect(() => { getAllItems(); }, []);

  const handleTrackInboundNavigation = () => {
    localStorage.removeItem("trackingId");
    localStorage.removeItem("newItems");
    navigate("/warehouse/inbound/track-inbound-item");
  };

  const filteredData = searchTerm.trim()
    ? inboundDetails.filter(
        (item) =>
          (item.trackingId && item.trackingId.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (item.shipmentId && String(item.shipmentId).toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : inboundDetails;

  const paginatedRows = filteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const columns = [
    { key: "trackingId", label: "Tracking ID", width: 200 },
    { key: "shipmentId", label: "Shipment ID", width: 150 },
    { key: "createdDate", label: "Shipment Date", width: 200 },
    {
      key: "action",
      label: "Action",
      width: 80,
      align: "center",
      render: (row) => (
        <Button
          variant="outline"
          size="sm"
          title="View Details"
          onClick={(e) => {
            e.stopPropagation();
            handleViewInbound(row);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            height: 32,
            fontSize: 12,
            fontWeight: 600,
            padding: isMobile ? "0 12px" : "0 8px",
            borderRadius: 8,
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Eye size={14} />
          {isMobile && <span>View Details</span>}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <PageHeader title="Inbound Items" subtitle="Track the status of your incoming shipment" />
        {isMobile ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <SearchBar
                  placeholder="Search tracking or shipment ID…"
                  searchTerm={searchTerm}
                  setSearchTerm={(v) => { setSearchTerm(v); setCurrentPage(0); }}
                />
              </div>
              <RefreshButton onRefresh={getAllItems} spinning={showLoader} />
            </div>
            <Button
              onClick={handleTrackInboundNavigation}
              style={{ width: "100%", height: 38 }}
            >
              Track Inbound Item
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
            <RefreshButton onRefresh={getAllItems} spinning={showLoader} />
            <SearchBar
              placeholder="Search tracking or shipment ID…"
              searchTerm={searchTerm}
              setSearchTerm={(v) => { setSearchTerm(v); setCurrentPage(0); }}
            />
            <Button onClick={handleTrackInboundNavigation}>
              Track Inbound Item
            </Button>
          </div>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={paginatedRows}
        totalRows={filteredData.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(n) => setCurrentPage(n)}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
        loading={showLoader}
        minWidth={600}
      />

      {/* View Inbound Item Dialog */}
      <Dialog open={Boolean(viewTrackingId)} onOpenChange={(open) => { if (!open) { setViewTrackingId(null); setViewItems([]); } }}>
        <DialogContent style={{ maxWidth: 900, width: isMobile ? "calc(100vw - 16px)" : "90vw" }}>
          <DialogHeader>
            <DialogTitle>Inbound Item</DialogTitle>
            <DialogDescription>Tracking ID: {viewTrackingId}</DialogDescription>
          </DialogHeader>

          <div style={{ marginBottom: 10 }}>
            {isMobile ? (
              <div style={{ display: "flex", gap: 8, alignItems: "center", width: "100%" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SearchBar
                    placeholder="Search by UPC…"
                    searchTerm={viewSearch}
                    setSearchTerm={(v) => { setViewSearch(v); setViewPage(0); }}
                  />
                </div>
                <RefreshButton onRefresh={() => fetchInboundItems(viewTrackingId)} spinning={viewLoading} />
              </div>
            ) : (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <RefreshButton onRefresh={() => fetchInboundItems(viewTrackingId)} spinning={viewLoading} />
                  <SearchBar
                    placeholder="Search by UPC…"
                    searchTerm={viewSearch}
                    setSearchTerm={(v) => { setViewSearch(v); setViewPage(0); }}
                  />
                </div>
              </div>
            )}
          </div>

          <DataTable
            columns={viewItemColumns}
            rows={viewPaginatedItems}
            totalRows={viewFilteredItems.length}
            currentPage={viewPage}
            rowsPerPage={viewRpp}
            onPageChange={(n) => setViewPage(n)}
            onRowsPerPageChange={(n) => { setViewRpp(n); setViewPage(0); }}
            loading={viewLoading}
            minWidth={800}
          />
        </DialogContent>
      </Dialog>

      {/* Image carousel dialog */}
      <Dialog open={viewImagesOpen} onOpenChange={() => setViewImagesOpen(false)}>
        <DialogContent style={{ maxWidth: isMobile ? "95vw" : 520 }}>
          <DialogHeader>
            <DialogTitle>Images</DialogTitle>
          </DialogHeader>
          {viewImages.length > 0 ? (
            <>
              <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minHeight: isMobile ? 180 : 300, padding: "0 44px" }}>
                <button
                  onClick={() => setViewImageIdx((prev) => (prev > 0 ? prev - 1 : viewImages.length - 1))}
                  disabled={viewImages.length <= 1}
                  style={{ position: "absolute", left: 0, background: "none", border: "none", cursor: viewImages.length <= 1 ? "default" : "pointer", padding: 4, color: "white", opacity: viewImages.length <= 1 ? 0.3 : 1, display: "flex", alignItems: "center" }}
                >
                  <ChevronLeft size={28} />
                </button>
                <img
                  src={`data:${viewImages[viewImageIdx].content_type};base64,${viewImages[viewImageIdx].document_bytes}`}
                  alt={`Item image ${viewImageIdx + 1}`}
                  style={{ maxWidth: "100%", maxHeight: isMobile ? 180 : 350, borderRadius: 8, objectFit: "contain", display: "block" }}
                />
                <button
                  onClick={() => setViewImageIdx((prev) => (prev < viewImages.length - 1 ? prev + 1 : 0))}
                  disabled={viewImages.length <= 1}
                  style={{ position: "absolute", right: 0, background: "none", border: "none", cursor: viewImages.length <= 1 ? "default" : "pointer", padding: 4, color: "white", opacity: viewImages.length <= 1 ? 0.3 : 1, display: "flex", alignItems: "center" }}
                >
                  <ChevronRight size={28} />
                </button>
              </div>
              <div style={{ textAlign: "center", marginTop: 8, fontSize: 13, color: "#888" }}>
                {viewImageIdx + 1} / {viewImages.length}
              </div>
            </>
          ) : (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: isMobile ? 180 : 300, color: "#888" }}>
              No image available
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseInbound;
